/* eslint-disable no-debugger */
import { Holistic } from "@mediapipe/holistic";
import { Camera } from "utils/mediapipes-camera";
import { useRef, useState } from "react";
import { RECORDING } from 'constants'
import { data } from "@tensorflow/tfjs";
import { use } from "react";

export const useHolistic = () => {
    const [isInitializingHolistic, setIsInitializingHoolistic] = useState(false);
    const [isInitializingCamera, setIsInitializingCamera] = useState(false);

    const holisticRef = useRef(null);
    const cameraRef = useRef(null);
    const webcamRef = useRef(null);
    const mediaRecorderRef = useRef(null);

    const processFrame = useRef(false);
    const recordFrame = useRef(false);
    const processingHolisticFrame = useRef(false);
    const [progressHolisticFrame, setProgressHolisticFrame] = useState(0);

    const onFrameResult = useRef(null);
    const recordedMedia = useRef([]);
    const recordedLandmarks = useRef([]);
    const uploaded = useRef(true);

    // Added resolver for processing uploaded video frames
    let resolveHolisticResult = null;

    const reset = async () => {
        if (cameraRef.current)
            cameraRef.current.stop();
        processFrame.current = false;

        await stopRecording();

        recordedMedia.current = [];
        recordedLandmarks.current = [];
    };

    const onHolisticResults = (results) => {
        if (!results) {
            console.log('Holistic results received but no results');
            return;
        }
        if (!results.poseLandmarks) {
            console.log('Holistic results received but no poseLandmarks');
            return;
        }

        const {
            poseLandmarks: {
                15: { x: x15, y: y15 },
                17: { x: x17, y: y17 },
                19: { x: x19, y: y19 },
                16: { x: x16, y: y16 },
                18: { x: x18, y: y18 },
                20: { x: x20, y: y20 },
                2: { x: x2, y: y2 },
                5: { x: x5, y: y5 },
            },
        } = results;

        if (onFrameResult.current)
            onFrameResult.current(results)

        if (uploaded){
            recordedLandmarks.current.push(results)
            return;
        }
        if (!uploaded){
            const left_hand_x = (x15 + x17 + x19) / 3;
            const left_hand_y = (y15 + y17 + y19) / 3;

            const right_hand_x = (x16 + x18 + x20) / 3;
            const right_hand_y = (y16 + y18 + y20) / 3;

            const eye_x = (x2 + x5) / 2;
            const eye_y = (y2 + y5) / 2;

            const inStartPosition = {
                leftHand: false,
                rightHand: false,
                //face: false,
                all: false
            }

            // if (Math.abs(eye_x - 0.480) < 0.080 && Math.abs(eye_y - 0.232) < 0.089) {
            //     //console.log('faceTemplate = ' + JSON.stringify(results.faceLandmarks))
            //     inStartPosition.face = true;
            // }

            if (Math.abs(left_hand_x - 0.635) < 0.075 && Math.abs(left_hand_y - 0.688) < 0.134) {
                //console.log('leftHandTemplate = ' + JSON.stringify(results.leftHandLandmarks))
                inStartPosition.leftHand = true;
            }

            if (Math.abs(right_hand_x - 0.365) < 0.075 && Math.abs(right_hand_y - 0.688) < 0.134) {
                //console.log('rightHandTemplate = ' + JSON.stringify(results.rightHandLandmarks))
                inStartPosition.rightHand = true;
            }

            inStartPosition.all = inStartPosition.leftHand && inStartPosition.rightHand; // && inStartPosition.face;

            results.inStartPosition = inStartPosition;

            if (onFrameResult.current)
                onFrameResult.current(results)

            // if (processingHolisticFrame.current)
            //     recordedLandmarks.current.push(results)
            recordedLandmarks.current.push(results)
        };
    };

    const initHolistic = async (onFrameResultFunc) => {
        setIsInitializingHoolistic(true);
        onFrameResult.current = onFrameResultFunc;

        if (!holisticRef.current) {
            const holistic = new Holistic({
                locateFile: (file) => {
                    return `./mediapipe/holistic/${file}`;
                },
            });

            holistic.setOptions({
                modelComplexity: 0,
                useCpuInference: true,
            });

            holisticRef.current = holistic;
        }

        holisticRef.current.onResults(onHolisticResults);

        setIsInitializingHoolistic(false);
    };

    const initCamera = async (deviceId) => {
        if (!webcamRef.current)
            throw new Error('Camera not available');

        if (cameraRef.current) {
            cameraRef.current.stop();
            cameraRef.current = null;
        }

        cameraRef.current = new Camera(webcamRef.current.video, {
            onFrame: async () => {
                if (!webcamRef.current) return;
                if (!holisticRef.current) return;
                if (!processFrame.current) return;
                if (processingHolisticFrame.current) return;

                try {
                    await holisticRef.current.send({ image: webcamRef.current.video });
                } catch (ex) {
                    console.log(ex);
                }
            },
        });
        await cameraRef.current.start(deviceId);
        processFrame.current = true;
        setIsInitializingCamera(true);
    };

    const startRecording = () => {
        recordedMedia.current = [];
        recordedLandmarks.current = [];
        processingHolisticFrame.current = false;

        if (webcamRef.current && webcamRef.current.stream) {
            mediaRecorderRef.current = new MediaRecorder(cameraRef.current.g, {
                mimeType: "video/webm"
            });

            mediaRecorderRef.current.ondataavailable = function ({ data }) {
                if (data.size > 0) {
                    recordedMedia.current.push(data);
                }
            };

            mediaRecorderRef.current.start(RECORDING.FPS);
        }

        if (cameraRef.current && cameraRef.current.startRecording) {
            cameraRef.current.startRecording();
        }

        recordFrame.current = true;
    };

    const stopRecording = async () => {
        recordFrame.current = false;

        var offlineRecording = [];

        if (cameraRef.current && cameraRef.current.startRecording) {
            offlineRecording = cameraRef.current.stopRecording();
        }

        if (mediaRecorderRef.current) {
            mediaRecorderRef.current.stop();
            mediaRecorderRef.current.ondataavailable = undefined;
        }
        const blob = new Blob(recordedMedia.current, {
            type: "video/webm"
        });

        const url = URL.createObjectURL(blob);

        processingHolisticFrame.current = true;
        for (var i = 0; i < offlineRecording.length; i++) {
            await holisticRef.current.send({ image: offlineRecording[i] });
        }
        processingHolisticFrame.current = false;

        return {
            uuid: Date.now(),
            landmarks: recordedLandmarks.current,
            media: recordedMedia.current,
            frames: offlineRecording,
            mediaBlob: blob,
            mediaUrl: url,
        };
    };


    async function processUploadedVideo(file) {
        try {
          const url = URL.createObjectURL(file);
          const video = document.createElement('video');
          video.src = url;
      
          // Ensure video is loaded
          await new Promise((resolve) => {
            video.onloadedmetadata = () => resolve();
          });
      
          const width = video.videoWidth;
          const height = video.videoHeight;
      
          // Create a canvas to capture frames
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const context = canvas.getContext('2d');
      
          const frameRate = 30; // Frames per second you want to process
          const duration = video.duration;
          const totalFrames = Math.floor(duration * frameRate);
      
          const frameSkipRate = duration < 1.5 ? 1 : 3;
      
          recordedLandmarks.current = []; // Ensure this is cleared for new uploads
          recordedMedia.current = []; // Ensure this is cleared for new uploads
          processingHolisticFrame.current = true; // Mark as processing
            
          // Loop through all the frames
          for (let i = 0; i < totalFrames; i += frameSkipRate) {
            // Set the current time to the correct frame based on the frame rate
            video.currentTime = i / frameRate;
      
            // Wait for the frame to be updated
            await new Promise((resolve) => {
              video.onseeked = () => {
                context.drawImage(video, 0, 0, width, height);
                resolve(); // Move to the next frame after drawing it on canvas
              };
            });
      
            // Wait for the holistic model to process the frame
            try {
              await holisticRef.current.send({ image: canvas });
            } catch (error) {
              console.error(`Error processing frame ${i + 1}:`, error);
            }
          }
      
          processingHolisticFrame.current = false; // Mark processing as complete
          processFrame.current = true; // Stop processing frames
          //URL.revokeObjectURL(url); // Clean up the object URL
            
          // Return the processed data
          return {
            uuid: Date.now(),
            landmarks: recordedLandmarks.current,
            mediaBlob: file,
            mediaUrl: url,
          };
        } catch (error) {
          console.error("Error processing video:", error);
          throw error; // Re-throw the error for the calling function to handle
        }
      }
      

    // Function to handle video upload event
    const handleVideoUpload = async (event) => {
        const file = event.target.files[0];
        if (file) {
            const data = await processUploadedVideo(file);
            return data;
        }
    };

    return {
        reset,
        webcamRef,

        isInitializingHolistic,
        initHolistic,
        holisticRef,

        isInitializingCamera,
        initCamera,
        cameraRef,
        processFrame,

        startRecording,
        stopRecording,
        recordFrame,

        progressHolisticFrame,
        processingHolisticFrame,

        // Expose the function to handle video upload
        handleVideoUpload,
        processUploadedVideo,
    };
};
